import { FilterInput, SearchCombobox } from '@zarya/ui';
import { t } from '@app/i18n';
import {
  ACTIONS_KNOWN,
  ENTITY_KINDS,
  type AuditFilterValues,
  type EntityKind,
  type ResultFilter,
} from './AuditModel';

export interface AuditFiltersProps {
  filtros: AuditFilterValues;
  onChange: (mudanca: Partial<AuditFilterValues>) => void;
}

/**
 * Filtros de T23 (FR-113): período, usuário, tipo de entidade, ação e resultado.
 *
 * Cada filtro tem o próprio «×» (ajustes-3, 28/08, item 15.1): `FilterInput` nos
 * campos de texto/data e `clearable` nos comboboxes. O «Limpar filtros» da
 * barra continua limpando tudo de uma vez.
 *
 * A ação é um combobox `creatable`: a lista sugerida vem do catálogo de
 * `data-model.md`, mas quem define as ações que existem é o backend — digitar
 * uma que não está na lista continua sendo um filtro válido.
 *
 * Usuário aceita `*` como curinga (item 15.3): `*approver*` casa
 * `approver@poc.local`. Sem `*` é igualdade exata, como o contrato descreve — e
 * o filtro vai à fonte nos dois casos (`GET /audit?usuario=`).
 */
export function AuditFilters({ filtros, onChange }: AuditFiltersProps) {
  return (
    <>
      <FilterInput
        label={t('audit.filters.from')}
        type="date"
        value={filtros.desde}
        max={filtros.ate || undefined}
        onChange={(desde) => onChange({ desde })}
      />

      <FilterInput
        label={t('audit.filters.to')}
        type="date"
        value={filtros.ate}
        min={filtros.desde || undefined}
        onChange={(ate) => onChange({ ate })}
      />

      <FilterInput
        label={t('audit.filters.user')}
        type="text"
        placeholder={t('audit.filters.user.placeholder')}
        helperText={t('audit.filters.user.help')}
        value={filtros.user}
        onChange={(user) => onChange({ user })}
      />

      <SearchCombobox
        label={t('audit.filters.entityKind')}
        size="sm"
        clearable
        placeholder={t('audit.filters.entityKind.placeholder')}
        options={ENTITY_KINDS.map((tipo) => ({ value: tipo, label: tipo, mono: true }))}
        value={filtros.entity_kind}
        onChange={(valor) => onChange({ entity_kind: (valor as EntityKind | null) ?? null })}
      />

      <SearchCombobox
        label={t('audit.filters.action')}
        size="sm"
        clearable
        placeholder={t('audit.filters.action.placeholder')}
        creatable
        onCreate={(texto) => onChange({ action: texto })}
        options={ACTIONS_KNOWN.map((acao) => ({ value: acao, label: acao, mono: true }))}
        value={filtros.action}
        onChange={(valor) => onChange({ action: valor })}
      />

      <SearchCombobox
        label={t('audit.filters.result')}
        size="sm"
        clearable
        placeholder={t('audit.filters.result.placeholder')}
        options={[
          { value: 'sucesso', label: t('audit.filters.result.success') },
          { value: 'falha', label: t('audit.filters.result.failure') },
        ]}
        value={filtros.result}
        onChange={(valor) => onChange({ result: (valor as ResultFilter | null) ?? null })}
      />
    </>
  );
}
